import Icon1 from "@assets/icons/Icon";
import { Text1 } from "@components/ui/texts/Texts";
import { useState, useEffect, useRef, useCallback } from "react";
import { Broadcast, SignatureRequest } from "types";
import { navigateTo } from "context/navigation.service";

import { useDataStore } from "@store/user_org";
import { CreateProjectModal } from "@components/modals/CreateProjectModal";
import { useParams, useSearchParams } from "react-router-dom";
import { Project, getProjectDisplayName } from "types";
import { StatusBlock } from "@components/ui/blocks/Status";
import { ChunkyTabs } from "@components/ui/selection/ChunkyTabs";
import { VerticalTimeline } from "@components/ui/blocks/VericalTimeline";
import { StepsList } from "@components/ui/blocks/StepsList";
import ProjectScreenSkeleton from "./ProjectScreenSkeleton";
import SearchField from "@components/ui/textfields/SearchField";
import { PhotoModal } from "@components/modals/PhotoModal";
import { DocumentSection } from "@components/ui/blocks/DocumentSection";
import { PartnersSection } from "@components/ui/blocks/PartnersSection";
import { useProjectStore } from "@store/projectStore";
import { resolveProjectPartners } from "@utils/resolvePartner";
import { isGUEST, isADMIN, isDESSINATEUR, cannotSeeFacturation } from "@helpers/accessHelpers";
import { ActionsMenuButtonProject } from "@components/ui/buttons/ActionsMenuButtonProject";
import { CustomImage } from "@components/ui/images/CustomImage";
import { InfosSection } from "@components/ui/blocks/InfosSection";
import { PlanningTable } from "@components/ui/blocks/PlanningTable";
import { PlanningSetupModal } from "@components/modals/PlanningSetupModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { Planning } from "types/planning";
import { planningsApi } from "@services/api/plannings";
import { syncEmailsApi } from "@services/api/syncEmails";
import { emailsSentApi } from "@services/api/emailsSent";
import { SyncEmail } from "types/sync_email";
import { InboxEmailModal } from "@components/modals/InboxEmailModal";
import { EmailSentModal } from "@components/modals/EmailSentModal";
import { EmailModal } from "@components/modals/EmailModal";
import { EmailSent } from "types";
import { Timestamp } from "firebase/firestore";
import { BillingTab } from "@components/ui/billing/BillingTab";
import { showNotification } from "context/notification.service";
import { projectsApi } from "@services/api/projects";
import { signatureRequestsApi } from "@services/api/signatureRequests";
import { SignatureStatusModal } from "@components/modals/SignatureStatusModal";
import { useReactToPrint } from "react-to-print";
import { SignatureSetupModal } from "@components/modals/SignatureSetupModal";
import { DropFileModal } from "@components/modals/DropFileModal";
import { moveDocumentToFolder, getDocumentFolderPath } from "@helpers/documentHelpers";
import { ProjectDocument } from "types";

export default function ProjectScreen() {
  const orgData = useDataStore((state) => state.orgData);
  const userData = useDataStore((state) => state.userData);
  const isGuest = isGUEST(userData);
  const isAdmin = isADMIN(userData);
  const isDessinateur = isDESSINATEUR(userData);
  const hideFacturation = cannotSeeFacturation(userData);
  const canResend = !isGuest;
  const {
    sentEmails,
    setSentEmails,
    fetchProject,
    fetchSentEmails,
    updateProject,
    plannings,
    fetchPlannings,
    addPlanning,
    updatePlanning,
    removePlannings,
    broadcasts,
    partners: storePartners,
  } = useProjectStore();
  const [expandedBroadcast, setExpandedBroadcast] = useState<string | null>(
    null,
  );
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const id = params.id;
  const [isLoading, setIsLoading] = useState(true);
  const [project, setProject] = useState<Project | undefined>(undefined);

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      fetchProject(id).then((p) => {
        setProject(p);
        setIsLoading(false);
      });
      if (orgData?.id) {
        fetchSentEmails(orgData.id, id);
        fetchPlannings(orgData.id, id);
        syncEmailsApi.list({ project_id: id }).then(setProjectInboxEmails).catch(console.error);
      }
      signatureRequestsApi.listForProject(id).then(setSignatureRequests).catch(console.error);
    }
  }, [id, orgData?.id]);

  // Poll for project + signature request updates (guest users see stale data otherwise)
  useEffect(() => {
    if (!id) return;
    const interval = setInterval(() => {
      fetchProject(id).then((p) => {
        if (p) setProject(p);
      });
      signatureRequestsApi.listForProject(id).then(setSignatureRequests).catch(console.error);
    }, 30000);
    return () => clearInterval(interval);
  }, [id]);
  const [createFolderOpen, setCreateFolderOpen] = useState<boolean>(false);
  const [timelineSearch, setTimelineSearch] = useState<string>("");
  const [planningSetupOpen, setPlanningSetupOpen] = useState(false);
  const [planningEditOpen, setPlanningEditOpen] = useState(false);
  const [deletingPlanning, setDeletingPlanning] = useState(false);
  const planningPrintRef = useRef<HTMLDivElement>(null);

  const projectPlannings = plannings.filter((p) => p.project_id === id);
  const activePlanning = projectPlannings[0] as Planning | undefined;

  const handlePrintPlanning = useReactToPrint({
    contentRef: planningPrintRef,
    documentTitle: `Planning — ${project ? getProjectDisplayName(project) : ""}`,
    pageStyle: `
      @page { size: landscape; margin: 5mm; }
      @media print {
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        .planning-print-header {
          display: block !important;
          border-bottom: 1px solid #e5e5e5;
        }
        .planning-print-container {
          overflow: visible !important;
          max-height: none !important;
          border-radius: 0 !important;
        }
        .planning-print-container table {
          font-size: 7px !important;
        }
        .planning-print-container td {
          padding: 0px 1px !important;
          position: static !important;
          z-index: auto !important;
        }
        .planning-print-container thead {
          position: static !important;
        }
      }
    `,
  });

  const [signatureRequests, setSignatureRequests] = useState<SignatureRequest[]>([]);
  const [selectedSignatureRequest, setSelectedSignatureRequest] = useState<SignatureRequest | null>(null);
  const [signatureSetup, setSignatureSetup] = useState<{
    fileId: string;
    pdfUrl: string;
    documentName: string;
  } | null>(null);

  const initialTab = searchParams.get("tab");
  const initialSubTab = searchParams.get("sub");
  const openSignatureId = searchParams.get("sig");
  const [activeTab, setActiveTab] = useState<string>(initialTab || "timeline");
  const [activePhotoTab, setActivePhotoTab] = useState<string>("date");
  const [activeDocumentTab, setActiveDocumentTab] = useState<string>(
    initialTab === "documents" && initialSubTab ? initialSubTab : "folder",
  );
  const [activeEmailTab, setActiveEmailTab] = useState<string>("received");
  const [projectInboxEmails, setProjectInboxEmails] = useState<SyncEmail[]>([]);
  const [selectedInboxEmail, setSelectedInboxEmail] = useState<SyncEmail | null>(null);
  const [selectedSentEmailId, setSelectedSentEmailId] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState<{ to: string[]; subject: string; body: string } | null>(null);
  const [addDocToFolderId, setAddDocToFolderId] = useState<string | null>(null);

  const handleSetProject = (updated: Project) => {
    setProject(updated);
    updateProject(updated);
  };

  // Deep-link: open a specific signature request from ?sig=<id>
  useEffect(() => {
    if (!openSignatureId || signatureRequests.length === 0) return;
    const match = signatureRequests.find((r) => r.id === openSignatureId);
    if (match) {
      setActiveTab("documents");
      setActiveDocumentTab("signatures");
      setSelectedSignatureRequest(match);
    }
  }, [openSignatureId, signatureRequests]);

  // Clear the per-category "unseen client actions" counters for a given tab.
  //
  // Rules:
  // - Photos tab clears "photos".
  // - Emails tab clears "emails".
  // - Documents tab clears BOTH "documents" and "signatures" — signatures
  //   live as a sub-tab inside Documents, so the parent tab badge sums
  //   both and we zero both at once.
  //
  // Implementation: uses the functional form of setProject so we never
  // capture a stale `project` via closure, and fires markSeen on the
  // backend in parallel. Called from two places:
  //   1. The setActiveTab click handler below, synchronously on click
  //      (guarantees the badge disappears immediately).
  //   2. A mount-time useEffect below, for direct URL arrivals like
  //      ?tab=documents.
  const clearUnseenForTab = useCallback(
    (tabValue: string) => {
      if (isGuest || !id || !userData?.id) return;
      const categories: ("photos" | "documents" | "emails" | "signatures")[] =
        tabValue === "photos"
          ? ["photos"]
          : tabValue === "emails"
          ? ["emails"]
          : tabValue === "documents"
          ? ["documents", "signatures"]
          : [];
      if (categories.length === 0) return;

      setProject((prev) => {
        if (!prev) return prev;
        const entry = prev.unseen_by_user?.[userData.id] || {};
        const nonZero = categories.filter(
          (c) => ((entry as any)[c] || 0) > 0,
        );
        if (nonZero.length === 0) return prev;
        const updatedEntry: Record<string, number> = { ...entry };
        for (const c of nonZero) updatedEntry[c] = 0;
        const updated = { ...(prev.unseen_by_user || {}) };
        updated[userData.id] = updatedEntry;
        // Fire-and-forget backend mark-seen for each cleared category.
        for (const c of nonZero) {
          projectsApi.markSeen(id, c).catch(() => {});
        }
        return { ...prev, unseen_by_user: updated };
      });
    },
    [id, isGuest, userData?.id],
  );

  // Mount / URL-driven activation: if the user lands directly on a tab
  // (e.g. deep link with ?tab=documents), clear that tab's counters once
  // the project finishes loading.
  useEffect(() => {
    if (!project) return;
    clearUnseenForTab(activeTab);
    // Deliberately NOT depending on activeTab — the click handler takes
    // care of subsequent changes. Depending on activeTab here would cause
    // the effect to race with the setState inside clearUnseenForTab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, clearUnseenForTab]);

  const markAllEmailsRead = () => {
    // Mark unread inbox emails as read
    const unreadInbox = projectInboxEmails.filter((e) => !e.is_read);
    if (unreadInbox.length > 0) {
      setProjectInboxEmails((prev) => prev.map((e) => ({ ...e, is_read: true })));
      unreadInbox.forEach((e) => syncEmailsApi.markAsRead(e.id).catch(() => {}));
    }
    // Mark unread sent emails (from client) as read
    const unreadSent = sentEmails.filter((e) => e.project_id === id && e.is_read === false);
    if (unreadSent.length > 0) {
      setSentEmails(sentEmails.map((e) => e.project_id === id && e.is_read === false ? { ...e, is_read: true } : e));
      unreadSent.forEach((e) => emailsSentApi.markAsRead(e.id).catch(() => {}));
    }
  };

  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);

  const handleRequestSignature = (doc: ProjectDocument) => {
    if (doc.file?.id && doc.file?.url) {
      // eslint-disable-next-line no-console
      console.log("[sig.handleRequestSignature]", {
        doc_id: doc.id,
        doc_name: doc.name,
        file_id: doc.file.id,
      });
      setSignatureSetup({
        fileId: doc.file.id,
        pdfUrl: doc.file.url,
        documentName: doc.name,
      });
    }
  };

  const facadeInputRef = useRef<HTMLInputElement>(null);
  const handleFacadeUpload = async (file: File) => {
    if (!project) return;
    try {
      const updatedProject = await projectsApi.addPhotos(project.id, [file]);
      const existingIds = new Set(project.photos.map((p) => p.id));
      const newPhoto = updatedProject.photos.find((p) => !existingIds.has(p.id));
      if (newPhoto) {
        updatedProject.infos.general.photo_file = newPhoto.file;
      }
      handleSetProject(updatedProject);
      showNotification({ type: "success", message: "Photo de façade ajoutée" });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'upload" });
    }
  };

  if (isLoading || !project) {
    return <ProjectScreenSkeleton />;
  }
  return (
    <div className="flex flex-col bg-neutral-100 overflow-auto max-h-screen">
      {signatureSetup && (
        <SignatureSetupModal
          onClose={() => setSignatureSetup(null)}
          project={project}
          fileId={signatureSetup.fileId}
          pdfUrl={signatureSetup.pdfUrl}
          documentName={signatureSetup.documentName}
          onComplete={(newRequest?: SignatureRequest) => {
            setSignatureSetup(null);
            setActiveTab("documents");
            setActiveDocumentTab("signatures");
            if (newRequest) {
              setSignatureRequests((prev) => [newRequest, ...prev]);
            }
            if (id) {
              fetchProject(id).then((p) => { if (p) setProject(p); });
            }
          }}
        />
      )}
      {createFolderOpen && (
        <CreateProjectModal
          onClose={() => setCreateFolderOpen(false)}
        ></CreateProjectModal>
      )}
      {selectedPhoto && (
        <PhotoModal
          onClose={() => setSelectedPhoto(null)}
          photo_id={selectedPhoto}
          activeProject={project}
          setActiveProject={handleSetProject}
        />
      )}
      {addDocToFolderId && (
        <DropFileModal
          onClose={() => setAddDocToFolderId(null)}
          onSave={async (data) => {
            setAddDocToFolderId(null);
            try {
              const parts = addDocToFolderId.split("/");
              const folderId = parts[0];
              const subfolderId = parts[1];
              const updatedProject = await projectsApi.addDocuments(
                project.id,
                data.files,
                folderId,
                subfolderId,
                data.internalDocument,
              );
              handleSetProject(updatedProject);
              showNotification({
                type: "success",
                message: `${data.files.length} document${data.files.length > 1 ? "s" : ""} ajouté${data.files.length > 1 ? "s" : ""}`,
              });

              if (data.electronicSignature && data.signers && data.signers.length > 0) {
                const allDocs: { id: string; name: string; file: { id: string; url: string; name: string } }[] = [];
                for (const folder of updatedProject.folders) {
                  for (const doc of folder.documents || []) allDocs.push(doc);
                  for (const sub of folder.sub_folders || []) {
                    for (const doc of sub.documents || []) allDocs.push(doc);
                  }
                }
                const uploadedDocs = data.files
                  .map((f) => allDocs.find((d) => d.name === f.name))
                  .filter((d) => d?.file?.id)
                  .map((d) => ({ fileId: d!.file.id, documentName: d!.name }));
                const signerPayload = data.signers.map((s) => ({
                  id: s.id,
                  signer_name: s.signer_name,
                  signer_email: s.signer_email,
                  signer_type: s.signer_type,
                }));

                // Inline-mode payload: the drop modal only allows it with
                // a single PDF + single signer, so at most one doc here.
                const useInline = data.placementMode === "inline_fields";
                const inlineApiFields = useInline && data.inlineFields
                  ? data.inlineFields.map((f) => ({
                      type: f.type as "Signature" | "Text" | "Date" | "Tampon",
                      x: f.left,
                      y: f.top,
                      width: f.width,
                      height: f.height,
                      page: f.page,
                      label: f.name,
                      content: f.content,
                      signer_id: f.signer_id,
                    }))
                  : undefined;

                let successCount = 0;
                const createdRequests: SignatureRequest[] = [];
                for (const doc of uploadedDocs) {
                  try {
                    const req = await signatureRequestsApi.create({
                      project_id: project.id,
                      org_id: orgData?.id || "",
                      document_name: doc.documentName,
                      file_id: doc.fileId,
                      signers: signerPayload,
                      placement_mode: useInline ? "inline_fields" : "appended_page",
                      fields: useInline ? inlineApiFields : undefined,
                      target_width: useInline ? data.inlineDimensions?.width : undefined,
                      target_height: useInline ? data.inlineDimensions?.height : undefined,
                    });
                    createdRequests.push(req);
                    successCount++;
                  } catch (e) {
                    console.error(`Failed to create signature for ${doc.documentName}:`, e);
                  }
                }
                if (successCount > 0) {
                  setSignatureRequests((prev) => [...createdRequests, ...prev]);
                  setActiveTab("documents");
                  setActiveDocumentTab("signatures");
                  showNotification({
                    type: "success",
                    message: `${successCount} document${successCount > 1 ? "s" : ""} envoyé${successCount > 1 ? "s" : ""} pour signature`,
                  });
                }
                if (successCount < uploadedDocs.length) {
                  showNotification({
                    type: "error",
                    message: `Échec de l'envoi pour ${uploadedDocs.length - successCount} document(s)`,
                  });
                }
              }
            } catch {
              showNotification({ type: "error", message: "Erreur lors de l'ajout du document" });
            }
          }}
          showRepertorySelector={false}
          showElectronicSignature={!isGuest}
          showInternalDocument={!isGuest}
          project={project}
        />
      )}
      <div className="flex flex-col h-fit pb-96 mt-10 mx-4 lg:mx-10">
        <div className="flex flex-col w-full max-w-1p4k">
          <div className="flex flex-col md:flex-row w-full justify-start mb-10">
            <div className="flex flex-col w-fit mr-4 gap-2 ">
              <div
                className="text-h3 text-neutral-400 hover:text-neutral-600 cursor-pointer"
                onClick={() => navigateTo({ route: "/projects" })}
              >
                Projets
              </div>
              <div className="flex flex-row items-center gap-3">
                <Text1>{project ? getProjectDisplayName(project) : "Projet sans nom"}</Text1>
              </div>
              <StatusBlock type="info" label="Terrassement"></StatusBlock>{" "}
              <StatusBlock type="success" label="Accès chantier"></StatusBlock>
            </div>
            <input
              ref={facadeInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFacadeUpload(file);
                e.target.value = "";
              }}
            />
            {project?.infos.general.photo_file?.url ? (
              <img
                src={project.infos.general.photo_file.url}
                className={`h-28 w-56 rounded-3xl object-cover mr-auto ${!isGuest ? "cursor-pointer hover:opacity-80 transition-opacity" : ""}`}
                onClick={() => !isGuest && facadeInputRef.current?.click()}
              />
            ) : (
              <div
                className={`h-28 w-56 rounded-3xl bg-white mr-auto flex items-center justify-center shadow-inner border border-neutral-200 ${!isGuest ? "cursor-pointer hover:bg-neutral-50 transition-colors" : ""}`}
                onClick={() => !isGuest && facadeInputRef.current?.click()}
              >
                <Icon1 name="image" size={24} color="grey" />
              </div>
            )}

            <ActionsMenuButtonProject
              project={project}
              setProject={setProject}
              isGuest={isGuest}
              onSignatureRequestCreated={() => {
                if (id) signatureRequestsApi.listForProject(id).then(setSignatureRequests).catch(console.error);
              }}
            />
          </div>

          {/* Active broadcasts banner (guest view) */}
          {isGuest &&
            (() => {
              const now = new Date();
              const activeBroadcasts = broadcasts.filter((b: Broadcast) => {
                if (b.status !== "active") return false;
                const start = b.start_date?.toDate
                  ? b.start_date.toDate()
                  : new Date((b.start_date as any).seconds * 1000);
                const end = b.end_date?.toDate
                  ? b.end_date.toDate()
                  : new Date((b.end_date as any).seconds * 1000);
                if (!(now >= start && now <= end)) return false;
                if (b.project_visibility && b.project_visibility.length > 0 && project) {
                  return b.project_visibility.includes(project.status as any);
                }
                return true;
              });
              if (activeBroadcasts.length === 0) return null;
              return (
                <div className="flex flex-col gap-2 mb-4 w-full max-w-1p4k">
                  {activeBroadcasts.map((b: Broadcast) => (
                    <div
                      key={b.id}
                      className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 cursor-pointer"
                      onClick={() =>
                        setExpandedBroadcast(
                          expandedBroadcast === b.id ? null : b.id,
                        )
                      }
                    >
                      <div className="flex flex-row items-center gap-2">
                        <Icon1 name="broadcast" size={16} color="blue" />
                        <div className="flex-1 text-h2 font-semibold text-blue-800">
                          {b.title}
                        </div>
                        <Icon1
                          name={expandedBroadcast === b.id ? "up" : "down"}
                          size={12}
                          color="blue"
                        />
                      </div>
                      {expandedBroadcast === b.id && (
                        <div className="mt-2 flex flex-col gap-1">
                          <div className="text-h3 text-blue-700 whitespace-pre-wrap">
                            {b.content}
                          </div>
                          <div className="text-h4 text-blue-400 mt-1">
                            Du{" "}
                            {(b.start_date?.toDate
                              ? b.start_date.toDate()
                              : new Date((b.start_date as any).seconds * 1000)
                            ).toLocaleDateString("fr-FR")}{" "}
                            au{" "}
                            {(b.end_date?.toDate
                              ? b.end_date.toDate()
                              : new Date((b.end_date as any).seconds * 1000)
                            ).toLocaleDateString("fr-FR")}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}

          <div className="flex flex-col lg:flex-row gap-8 items-start">
            <div className="flex flex-col flex-1 min-w-0">
              <ChunkyTabs
                tabs={(() => {
                  const baseTabs = isGuest ? guestProjectTabs : projectTabs;
                  let visibleTabs = project.billing
                    ? baseTabs
                    : baseTabs.filter((t) => t.value !== "facturation");
                  // Commercials and dessinateurs never see Facturation.
                  // Dessinateurs also lose access to Artisans.
                  if (hideFacturation) {
                    visibleTabs = visibleTabs.filter((t) => t.value !== "facturation");
                  }
                  if (isDessinateur) {
                    visibleTabs = visibleTabs.filter((t) => t.value !== "partners");
                  }
                  return visibleTabs;
                })().map((t) => {
                  if (t.value === "emails") {
                    const guestIds = new Set(project.guest_user_ids || []);
                    const unreadInbox = projectInboxEmails.filter((e) => !e.is_read).length;
                    const unreadClientEmails = sentEmails.filter((e) => e.project_id === id && e.is_read === false && guestIds.has(e.sender_user_id)).length;
                    return { ...t, badge: unreadInbox + unreadClientEmails };
                  }
                  if (!isGuest && userData?.id && (t.value === "photos" || t.value === "documents")) {
                    const entry = project.unseen_by_user?.[userData.id];
                    // Documents tab rolls up both documents and signatures, so
                    // the parent tab reflects all client-side doc activity.
                    const count =
                      t.value === "documents"
                        ? (entry?.documents || 0) + (entry?.signatures || 0)
                        : entry?.[t.value] || 0;
                    return count > 0 ? { ...t, badge: count } : t;
                  }
                  return t;
                })}
                activeTab={activeTab}
                setActiveTab={(value) => {
                  setActiveTab(value);
                  clearUnseenForTab(value);
                  if (value === "emails") markAllEmailsRead();
                }}
              ></ChunkyTabs>

              <div className="flex flex-col h-fit mt-4 gap-4">
                {activeTab === "timeline" && (
                  <div className="flex flex-col w-full max-w-500 gap-8">
                    <SearchField
                      value={timelineSearch}
                      onChange={(value) => setTimelineSearch(value)}
                      placeholder="Terrassement .."
                    />
                    <VerticalTimeline
                      history_events={project.history_events}
                      activeProject={project}
                      setActiveProject={handleSetProject}
                      sentEmails={sentEmails}
                      setSentEmails={setSentEmails}
                      userData={userData || undefined}
                      orgData={orgData || undefined}
                      search={timelineSearch}
                      isGuest={isGuest}
                    />
                  </div>
                )}
                {activeTab === "facturation" && project.billing && !hideFacturation && (
                  <BillingTab project={project} setProject={setProject} isGuest={isGuest} isAdmin={isAdmin} canResend={canResend} />
                )}
                {activeTab === "steps" && (
                  <StepsList
                    label=""
                    setProject={setProject}
                    project={project!}
                    userData={userData || undefined}
                    readOnly={isGuest || isDessinateur}
                    isGuest={isGuest}
                  />
                )}
                {activeTab === "photos" && (
                  <div className="flex flex-col w-full max-w-500 gap-8">
                    <ChunkyTabs
                      tabs={photoTabs}
                      activeTab={activePhotoTab}
                      setActiveTab={(value) => setActivePhotoTab(value)}
                    ></ChunkyTabs>
                    <div className="">
                      {activePhotoTab === "date" && (
                        <VerticalTimeline
                          history_events={project.history_events.filter(
                            (e) => e.photos_id && e.photos_id.length > 0,
                          )}
                          activeProject={project}
                          setActiveProject={handleSetProject}
                          sentEmails={sentEmails}
                          orgData={orgData || undefined}
                          userData={userData || undefined}
                          isGuest={isGuest}
                        ></VerticalTimeline>
                      )}
                      {activePhotoTab === "gallery" && (
                        <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] max-w-full gap-4">
                          {project.photos
                            .filter((photo) => (isGuest ? !photo.hidden : true))
                            .slice()
                            .reverse()
                            .map((photo, index) => (
                              <CustomImage
                                key={index}
                                source={{ uri: photo.file.url }}
                                className="w-full aspect-square rounded-xl cursor-pointer"
                                hidden={photo.hidden}
                                alt={`Photo ${index + 1}`}
                                onClick={() => setSelectedPhoto(photo.id)}
                              />
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {activeTab === "documents" && (
                  <div className="flex flex-col w-full max-w-500 gap-8">
                    <ChunkyTabs
                      tabs={(() => {
                        const base = documentTabs;
                        if (isGuest || !userData?.id) return base;
                        const entry = project.unseen_by_user?.[userData.id];
                        const sigCount = entry?.signatures || 0;
                        return base.map((t) =>
                          t.value === "signatures" && sigCount > 0
                            ? { ...t, badge: sigCount }
                            : t,
                        );
                      })()}
                      activeTab={activeDocumentTab}
                      setActiveTab={(value) => setActiveDocumentTab(value)}
                    ></ChunkyTabs>
                    <div className="">
                      {activeDocumentTab === "folder" && (
                        <DocumentSection
                          folders={project.folders}
                          activeProject={project}
                          setActiveProject={handleSetProject}
                          readOnly={isGuest || isDessinateur}
                          signatureRequests={signatureRequests}
                          onRequestSignature={!isGuest ? handleRequestSignature : undefined}
                          onAddDocumentToFolder={!isGuest ? (folderId) => setAddDocToFolderId(folderId) : undefined}
                          onMoveDocument={!isGuest ? (docId, targetPath) => {
                            const currentPath = getDocumentFolderPath(project, docId);
                            if (currentPath === targetPath) return;
                            const updated = moveDocumentToFolder(project, docId, targetPath);
                            handleSetProject(updated);
                            projectsApi.update(project.id, { folders: updated.folders }).catch((err) => {
                              console.error("Failed to move document:", err);
                            });
                            showNotification({ type: "success", message: "Document déplacé" });
                          } : undefined}
                        />
                      )}
                      {activeDocumentTab === "date" && (
                        <VerticalTimeline
                          history_events={project.history_events.filter(
                            (e) => e.documents_id && e.documents_id.length > 0,
                          )}
                          activeProject={project}
                          setActiveProject={handleSetProject}
                          sentEmails={sentEmails}
                          orgData={orgData || undefined}
                          isGuest={isGuest}
                        ></VerticalTimeline>
                      )}
                      {activeDocumentTab === "signatures" && (
                        <>
                          {selectedSignatureRequest && (
                            <SignatureStatusModal
                              onClose={() => {
                                setSelectedSignatureRequest(null);
                                // Drop ?sig=... so the deep-link effect
                                // doesn't immediately reopen the modal.
                                if (searchParams.has("sig")) {
                                  const next = new URLSearchParams(searchParams);
                                  next.delete("sig");
                                  setSearchParams(next, { replace: true });
                                }
                                // Refresh signature requests
                                if (id) {
                                  signatureRequestsApi.listForProject(id).then(setSignatureRequests).catch(console.error);
                                }
                              }}
                              signatureRequest={selectedSignatureRequest}
                            />
                          )}
                          {signatureRequests.length === 0 ? (
                            <div className="text-h3 text-neutral-400 text-center py-8">
                              Aucune demande de signature
                            </div>
                          ) : (
                            <div className="flex flex-col gap-2">
                              {signatureRequests.map((sr) => {
                                const signedCount = sr.signers.filter((s) => s.status === "signed" || s.status === "signed_in_app" || s.status === "signed_remote").length;
                                const totalCount = sr.signers.length;
                                return (
                                  <div
                                    key={sr.id}
                                    className="flex items-center gap-3 p-3 border rounded-xl cursor-pointer hover:bg-neutral-50 transition-colors"
                                    onClick={() => setSelectedSignatureRequest(sr)}
                                  >
                                    <Icon1 name="sign" size={20} />
                                    <div className="flex-1 min-w-0">
                                      <div className="text-h2 font-medium truncate">
                                        {sr.document_name}
                                      </div>
                                      <div className="text-h4 text-neutral-400">
                                        {new Date(sr.created_at).toLocaleDateString("fr-FR")}
                                      </div>
                                    </div>
                                    <StatusBlock
                                      type={sr.status === "completed" ? "success" : "info"}
                                      label={
                                        sr.status === "completed"
                                          ? "Terminé"
                                          : `${signedCount}/${totalCount} signé${signedCount > 1 ? "s" : ""}`
                                      }
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
                {activeTab === "partners" && (
                  <PartnersSection
                    project={project}
                    setProject={handleSetProject}
                    readOnly={isGuest || isDessinateur}
                  />
                )}
                {activeTab === "planning" && (
                  <div className="flex flex-col gap-4">
                    {planningSetupOpen && (
                      <PlanningSetupModal
                        onClose={() => setPlanningSetupOpen(false)}
                        onCreated={(p) => addPlanning(p)}
                        onProjectUpdated={(p) => setProject(p)}
                        projectId={id || ""}
                        orgId={orgData?.id || ""}
                        project={project}
                        partners={resolveProjectPartners(project, storePartners)}
                      />
                    )}
                    {planningEditOpen && activePlanning && (
                      <PlanningSetupModal
                        onClose={() => setPlanningEditOpen(false)}
                        onUpdated={(p) => updatePlanning(p)}
                        projectId={id || ""}
                        orgId={orgData?.id || ""}
                        project={project}
                        partners={resolveProjectPartners(project, storePartners)}
                        existingPlanning={activePlanning}
                      />
                    )}
                    {activePlanning ? (
                      <div className="flex flex-col gap-4">
                        <div className="flex items-center justify-between gap-4">
                          {!isGuest && (
                            <span className="text-h4 text-neutral-400 italic">
                              Ce planning est un planning prévisionnel
                              d'interventions et est susceptible d'évoluer.
                            </span>
                          )}
                          <div className="flex flex-row gap-2">
                            <SecondaryButton
                              variant="small"
                              onClick={() => handlePrintPlanning()}
                              startIcon={<Icon1 name="pdf" size={16} />}
                            >
                              Télécharger PDF
                            </SecondaryButton>
                            {!isGuest && (
                              <>
                                <SecondaryButton
                                  variant="small"
                                  danger
                                  loading={deletingPlanning}
                                  onClick={async () => {
                                    if (!activePlanning) return;
                                    setDeletingPlanning(true);
                                    try {
                                      await planningsApi.delete(activePlanning.id);
                                      removePlannings([activePlanning.id]);
                                      showNotification({
                                        type: "success",
                                        message: "Planning supprimé",
                                      });
                                    } catch (error) {
                                      showNotification({
                                        type: "error",
                                        message: "Échec de la suppression du planning",
                                      });
                                    } finally {
                                      setDeletingPlanning(false);
                                    }
                                  }}
                                >
                                  Supprimer
                                </SecondaryButton>
                                <Button
                                  label="Modifier le planning"
                                  onClick={() => setPlanningEditOpen(true)}
                                />
                              </>
                            )}
                          </div>
                        </div>
                        <PlanningTable
                          planning={activePlanning}
                          partners={resolveProjectPartners(project, storePartners)}
                          onUpdate={updatePlanning}
                          isGuest={isGuest}
                          printRef={planningPrintRef}
                          clientName={getProjectDisplayName(project)}
                          projectAddress={[
                            project.infos.general.address,
                            [project.infos.general.postal, project.infos.general.city]
                              .filter(Boolean)
                              .join(" "),
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        />
                      </div>
                    ) : (
                      <div className="flex flex-col items-start gap-4 py-8">
                        <div className="text-h2 text-neutral-600 font-medium">
                          Aucun planning prévisionnel
                        </div>
                        <div className="text-h3 text-neutral-400">
                          {isGuest
                            ? "Le planning n'a pas encore été créé."
                            : "Créez un planning pour suivre les interventions semaine par semaine."}
                        </div>
                        {!isGuest && (
                          <Button
                            label="Créer le planning"
                            onClick={() => setPlanningSetupOpen(true)}
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}
                {activeTab === "emails" && isGuest && (() => {
                  const userEmail = userData?.infos?.email?.toLowerCase();
                  const userId = userData?.id;
                  const mySentEmails = sentEmails
                    .filter((e) => e.project_id === id)
                    .filter((e) => {
                      const to = (e.emails_to || []).map((x: string) => x.toLowerCase());
                      const cc = (e.emails_cc || []).map((x: string) => x.toLowerCase());
                      const isRecipient = userEmail && (to.includes(userEmail) || cc.includes(userEmail));
                      const isSender = userId && e.sender_user_id === userId;
                      return isRecipient || isSender;
                    });
                  // Inbox emails the scheduler pulled from Gmail. We surface
                  // those whose sender matches the current client so the
                  // guest sees their own out-of-app replies in the thread.
                  const myInboxEmails = projectInboxEmails.filter(
                    (e) => userEmail && e.from_email?.toLowerCase() === userEmail,
                  );
                  const merged: Array<
                    | { type: "sent"; data: (typeof mySentEmails)[number]; ts: number }
                    | { type: "inbox"; data: (typeof myInboxEmails)[number]; ts: number }
                  > = [
                    ...mySentEmails.map((e) => ({
                      type: "sent" as const,
                      data: e,
                      ts: e.created_at?.seconds || 0,
                    })),
                    ...myInboxEmails.map((e) => ({
                      type: "inbox" as const,
                      data: e,
                      ts: e.received_at?.seconds || 0,
                    })),
                  ].sort((a, b) => b.ts - a.ts);
                  return (
                    <div className="flex flex-col w-full max-w-500 gap-4">
                      {selectedSentEmailId && (
                        <EmailSentModal
                          onClose={() => setSelectedSentEmailId(null)}
                          email_sent_id={selectedSentEmailId}
                          sentEmails={sentEmails}
                          activeProject={project}
                          orgData={orgData || undefined}
                        />
                      )}
                      {selectedInboxEmail && (
                        <InboxEmailModal
                          onClose={() => setSelectedInboxEmail(null)}
                          email={selectedInboxEmail}
                          isGuest
                        />
                      )}
                      <div className="flex flex-col rounded-lg border border-neutral-200 overflow-hidden bg-white">
                        {merged.length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-16 gap-2">
                            <Icon1 name="email" size={32} color="grey" />
                            <span className="text-sm text-neutral-400">Aucun email pour le moment.</span>
                          </div>
                        ) : (
                          merged.map((item) => {
                            if (item.type === "sent") {
                              const email = item.data;
                              return (
                                <div
                                  key={`sent-${email.id}`}
                                  onClick={() => setSelectedSentEmailId(email.id)}
                                  className="group flex flex-row items-center gap-3 px-4 py-3 border-b border-neutral-100 cursor-pointer bg-white hover:bg-neutral-50 transition-colors"
                                >
                                  <div className="flex flex-col flex-1 min-w-0 gap-0.5">
                                    <span className="text-sm font-semibold text-neutral-900 truncate">
                                      {email.subject || "(sans sujet)"}
                                    </span>
                                    <div className="text-xs text-neutral-400 truncate">
                                      {email.body?.replace(/\n/g, " ").trim().slice(0, 80) || "—"}
                                    </div>
                                  </div>
                                  <span className="text-xs text-neutral-400 flex-shrink-0">
                                    {email.created_at ? new Date(email.created_at.seconds * 1000).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "—"}
                                  </span>
                                </div>
                              );
                            }
                            const email = item.data;
                            return (
                              <div
                                key={`inbox-${email.id}`}
                                onClick={() => setSelectedInboxEmail(email)}
                                className="group flex flex-row items-center gap-3 px-4 py-3 border-b border-neutral-100 cursor-pointer bg-white hover:bg-neutral-50 transition-colors"
                              >
                                <div className="flex flex-col flex-1 min-w-0 gap-0.5">
                                  <div className="flex flex-row items-baseline gap-2 min-w-0">
                                    <span className="text-xs text-neutral-400 shrink-0">
                                      Votre réponse
                                    </span>
                                    <span className="text-sm font-semibold text-neutral-900 truncate">
                                      {email.subject || "(sans objet)"}
                                    </span>
                                  </div>
                                  <div className="text-xs text-neutral-400 truncate">
                                    {email.body_text?.replace(/\n/g, " ").trim().slice(0, 80) || "—"}
                                  </div>
                                </div>
                                <span className="text-xs text-neutral-400 flex-shrink-0">
                                  {email.received_at ? new Date(email.received_at.seconds * 1000).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "—"}
                                </span>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  );
                })()}
                {activeTab === "emails" && !isGuest && (
                  <div className="flex flex-col w-full max-w-500 gap-8">
                    {selectedInboxEmail && (
                      <InboxEmailModal
                        onClose={() => {
                          setSelectedInboxEmail(null);
                          if (id) syncEmailsApi.list({ project_id: id }).then(setProjectInboxEmails).catch(console.error);
                        }}
                        email={selectedInboxEmail}
                        inProjectContext
                      />
                    )}
                    {selectedSentEmailId && (() => {
                      const selected = sentEmails.find((e) => e.id === selectedSentEmailId);
                      const guestIds = new Set(project.guest_user_ids || []);
                      const isFromClient = !!selected && guestIds.has(selected.sender_user_id);
                      const handleReply = (email: EmailSent) => {
                        const contacts = project.infos?.client?.contacts || [];
                        const contact = contacts.find((c) => c.user_id === email.sender_user_id);
                        const toEmail = contact?.email;
                        if (!toEmail) {
                          showNotification({ type: "error", message: "Email du client introuvable" });
                          return;
                        }
                        const subject = (email.subject || "").trim();
                        const replySubject = subject.toLowerCase().startsWith("re:") ? subject : `Re: ${subject}`;
                        const dateStr = email.created_at ? new Date(email.created_at.seconds * 1000).toLocaleString("fr-FR") : "";
                        const senderName = contact ? `${contact.first_name || ""} ${contact.last_name || ""}`.trim() : "le client";
                        const quoted = (email.body || "").split("\n").map((l) => `> ${l}`).join("\n");
                        const signature = userData && orgData ? `${userData.infos.display_name}\n${orgData.infos.name}` : "";
                        const greeting = contact?.first_name ? `Bonjour ${contact.first_name},` : "Bonjour,";
                        const body = `${greeting}\n\n\n\n${signature}\n\n---\nLe ${dateStr}, ${senderName} a écrit :\n${quoted}`;
                        setSelectedSentEmailId(null);
                        setReplyDraft({ to: [toEmail], subject: replySubject, body });
                      };
                      return (
                        <EmailSentModal
                          onClose={() => setSelectedSentEmailId(null)}
                          email_sent_id={selectedSentEmailId}
                          sentEmails={sentEmails}
                          activeProject={project}
                          orgData={orgData || undefined}
                          onReply={isFromClient ? handleReply : undefined}
                        />
                      );
                    })()}
                    {replyDraft && (
                      <EmailModal
                        onClose={() => setReplyDraft(null)}
                        activeProject={project}
                        setActiveProject={handleSetProject}
                        userData={userData || undefined}
                        orgData={orgData || undefined}
                        sentEmails={sentEmails}
                        setSentEmails={setSentEmails}
                        isGuest={isGuest}
                        initialToEmails={replyDraft.to}
                        initialSubject={replyDraft.subject}
                        initialBody={replyDraft.body}
                      />
                    )}
                    <ChunkyTabs
                      tabs={emailTabs}
                      activeTab={activeEmailTab}
                      setActiveTab={(value) => setActiveEmailTab(value)}
                    />
                    {activeEmailTab === "received" && (() => {
                      const guestIds = new Set(project.guest_user_ids || []);
                      const clientSentEmails = sentEmails
                        .filter((e) => e.project_id === id && guestIds.has(e.sender_user_id));
                      const totalReceived = projectInboxEmails.length + clientSentEmails.length;
                      // Map guest user IDs to contact names for display
                      const contacts = project.infos?.client?.contacts || [];
                      const guestNameMap = new Map<string, string>();
                      for (const c of contacts) {
                        if (c.user_id) guestNameMap.set(c.user_id, `${c.first_name || ""} ${c.last_name || ""}`.trim());
                      }
                      // Merge inbox emails and client-sent emails into a unified list sorted by date
                      const allReceived: Array<{ type: "inbox"; data: (typeof projectInboxEmails)[0] } | { type: "client_sent"; data: (typeof clientSentEmails)[0] }> = [
                        ...projectInboxEmails.map((e) => ({ type: "inbox" as const, data: e })),
                        ...clientSentEmails.map((e) => ({ type: "client_sent" as const, data: e })),
                      ].sort((a, b) => {
                        const aTime = a.type === "inbox" ? (a.data.received_at?.seconds || 0) : (a.data.created_at?.seconds || 0);
                        const bTime = b.type === "inbox" ? (b.data.received_at?.seconds || 0) : (b.data.created_at?.seconds || 0);
                        return bTime - aTime;
                      });
                      return (
                      <div className="flex flex-col rounded-lg border border-neutral-200 overflow-hidden bg-white">
                        <div className="flex flex-row items-center gap-3 px-4 py-2 border-b border-neutral-200 bg-neutral-50">
                          <span className="text-xs text-neutral-500 flex-1">
                            {totalReceived} email{totalReceived !== 1 ? "s" : ""} reçu{totalReceived !== 1 ? "s" : ""}
                          </span>
                        </div>
                        {totalReceived === 0 ? (
                          <div className="flex flex-col items-center justify-center py-16 gap-2">
                            <Icon1 name="inbox" size={32} color="grey" />
                            <span className="text-sm text-neutral-400">Aucun email reçu pour ce projet.</span>
                          </div>
                        ) : (
                          allReceived.map((item) => {
                            if (item.type === "inbox") {
                              const email = item.data;
                              return (
                                <div
                                  key={email.id}
                                  onClick={() => setSelectedInboxEmail(email)}
                                  className={`group flex flex-row items-center gap-3 px-4 py-3 border-b border-neutral-100 cursor-pointer transition-colors ${
                                    email.is_read ? "bg-white hover:bg-neutral-50" : "bg-blue-50/30 hover:bg-blue-50/50"
                                  }`}
                                >
                                  <div className="flex-shrink-0 w-2">
                                    {!email.is_read && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                                  </div>
                                  <div className="flex flex-col flex-1 min-w-0 gap-0.5">
                                    <div className="flex flex-row items-baseline gap-2 min-w-0">
                                      <span className={`text-sm truncate shrink-0 max-w-48 ${email.is_read ? "text-neutral-700" : "font-semibold text-neutral-900"}`}>
                                        {email.from_name || email.from_email}
                                      </span>
                                      <span className={`text-sm truncate ${email.is_read ? "text-neutral-400" : "font-medium text-neutral-700"}`}>
                                        {email.subject || "(sans objet)"}
                                      </span>
                                    </div>
                                    <div className="text-xs text-neutral-400 truncate">
                                      {email.body_text?.replace(/\n/g, " ").trim() || "—"}
                                    </div>
                                  </div>
                                  <span className="text-xs text-neutral-400 flex-shrink-0">
                                    {email.received_at ? new Date(email.received_at.seconds * 1000).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "—"}
                                  </span>
                                </div>
                              );
                            }
                            const email = item.data;
                            return (
                              <div
                                key={email.id}
                                onClick={() => setSelectedSentEmailId(email.id)}
                                className={`group flex flex-row items-center gap-3 px-4 py-3 border-b border-neutral-100 cursor-pointer transition-colors ${
                                  email.is_read === false ? "bg-blue-50/30 hover:bg-blue-50/50" : "bg-white hover:bg-neutral-50"
                                }`}
                              >
                                <div className="flex-shrink-0 w-2">
                                  {email.is_read === false && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                                </div>
                                <div className="flex flex-col flex-1 min-w-0 gap-0.5">
                                  <div className="flex flex-row items-baseline gap-2 min-w-0">
                                    <span className={`text-sm truncate shrink-0 max-w-48 ${email.is_read === false ? "font-semibold text-neutral-900" : "text-neutral-700"}`}>
                                      {guestNameMap.get(email.sender_user_id) || "Client"}
                                    </span>
                                    <span className={`text-sm truncate ${email.is_read === false ? "font-medium text-neutral-700" : "text-neutral-400"}`}>
                                      {email.subject || "(sans sujet)"}
                                    </span>
                                  </div>
                                  <div className="text-xs text-neutral-400 truncate">
                                    {email.body?.replace(/\n/g, " ").trim() || "—"}
                                  </div>
                                </div>
                                <span className="text-xs text-neutral-400 flex-shrink-0">
                                  {email.created_at ? new Date(email.created_at.seconds * 1000).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "—"}
                                </span>
                              </div>
                            );
                          })
                        )}
                      </div>
                      );
                    })()}
                    {activeEmailTab === "sent" && (() => {
                      const guestIds = new Set(project.guest_user_ids || []);
                      const projectSentEmails = sentEmails.filter((e) => e.project_id === id && !guestIds.has(e.sender_user_id));
                      return (
                      <div className="flex flex-col rounded-lg border border-neutral-200 overflow-hidden bg-white">
                        <div className="flex flex-row items-center gap-3 px-4 py-2 border-b border-neutral-200 bg-neutral-50">
                          <span className="text-xs text-neutral-500 flex-1">
                            {projectSentEmails.length} email{projectSentEmails.length !== 1 ? "s" : ""} envoyé{projectSentEmails.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                        {projectSentEmails.length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-16 gap-2">
                            <Icon1 name="email" size={32} color="grey" />
                            <span className="text-sm text-neutral-400">Aucun email envoyé pour ce projet.</span>
                          </div>
                        ) : (
                          [...projectSentEmails]
                            .sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0))
                            .map((email) => (
                              <div
                                key={email.id}
                                onClick={() => setSelectedSentEmailId(email.id)}
                                className="group flex flex-row items-center gap-3 px-4 py-3 border-b border-neutral-100 cursor-pointer bg-white hover:bg-neutral-50 transition-colors"
                              >
                                <div className="flex flex-col flex-1 min-w-0 gap-0.5">
                                  <div className="flex flex-row items-baseline gap-2 min-w-0">
                                    <span className="text-sm font-semibold text-neutral-900 truncate shrink-0 max-w-xs">
                                      {email.subject || "(sans sujet)"}
                                    </span>
                                  </div>
                                  <div className="text-xs text-neutral-400 truncate">
                                    À : {email.emails_to?.join(", ") || "—"}
                                  </div>
                                </div>
                                <span className="text-xs text-neutral-400 flex-shrink-0">
                                  {email.created_at ? new Date(email.created_at.seconds * 1000).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "—"}
                                </span>
                              </div>
                            ))
                        )}
                      </div>
                      );
                    })()}
                  </div>
                )}

              </div>
            </div>

            {activeTab !== "planning" && activeTab !== "emails" && (
              <InfosSection project={project} setProject={handleSetProject} isGuest={isGuest} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const projectTabs = [
  { value: "timeline", label: "Fil du projet" },
  { value: "steps", label: "Étapes" },
  { value: "photos", label: "Photos" },
  { value: "documents", label: "Documents" },
  { value: "partners", label: "Artisans" },
  { value: "planning", label: "Planning" },
  { value: "facturation", label: "Facturation" },
  { value: "emails", label: "Emails" },
];

const guestProjectTabs = [
  { value: "timeline", label: "Fil du projet" },
  { value: "steps", label: "Étapes" },
  { value: "photos", label: "Photos" },
  { value: "documents", label: "Documents" },
  { value: "partners", label: "Artisans" },
  { value: "planning", label: "Planning" },
  { value: "facturation", label: "Mes factures" },
  { value: "emails", label: "Emails" },
];

const photoTabs = [
  {
    value: "date",
    label: "Date",
  },
  {
    value: "gallery",
    label: "Galerie",
  },
];

const emailTabs = [
  {
    value: "received",
    label: "Reçus",
  },
  {
    value: "sent",
    label: "Envoyés",
  },
];

const documentTabs = [
  {
    value: "folder",
    label: "Dossier",
  },
  {
    value: "date",
    label: "Date",
  },
  {
    value: "signatures",
    label: "Signatures",
  },
];
